import React from "react";
import { Box, Chip, CircularProgress, useTheme } from "@mui/material";
import { observer } from "mobx-react-lite";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import InlineFormError from "../../../../../../views/components/common/form/InlineFormError";
import { getModernPageColors } from "../../../../../../styles/modernListingPageSx";
import { OrderClientPO } from "../../../../../../models/Order/OrderClientPO";
import { OrderClientPOStore } from "../../../../stores/OrderClientPOStore";
import { saveClientPOFile } from "./clientPOFile";

interface ClientPOSummaryProps {
  store: OrderClientPOStore;
  /** Opens the Client POs popup. Hidden when the admin cannot edit POs. */
  onManage?: () => void;
}

/** Read-only PO chips on the order page. Click a chip to download its PDF. */
const ClientPOSummary: React.FC<ClientPOSummaryProps> = observer(
  ({ store, onManage }) => {
    const theme = useTheme();
    const colors = getModernPageColors(theme);

    const handleDownload = async (po: OrderClientPO) => {
      const response = await store.downloadClientPO(po);
      if (response) saveClientPOFile(response, po);
    };

    return (
      <Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 0.75,
            minHeight: 40,
          }}
        >
          {store.isLoading && store.clientPOs.length === 0 ? (
            <CircularProgress size={18} />
          ) : store.clientPOs.length === 0 ? (
            <CustomTypography variant="body2" color={colors.subtitle}>
              No client POs available
            </CustomTypography>
          ) : (
            store.clientPOs.map((po) => (
              <Chip
                key={po.id}
                size="small"
                label={po.po_number || "—"}
                title={po.po_url ? "Download PO" : "No PDF"}
                onClick={po.po_url ? () => void handleDownload(po) : undefined}
                icon={
                  store.downloadingId === po.id ? (
                    <CircularProgress size={12} />
                  ) : po.po_url ? (
                    <FileDownloadOutlinedIcon />
                  ) : undefined
                }
                sx={{
                  height: 26,
                  borderRadius: "999px",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: colors.accent,
                  bgcolor: colors.surfaceSoft,
                  border: `1px solid ${theme.palette.customColors.softBorder}`,
                  "& .MuiChip-icon": { color: colors.accent, fontSize: 16 },
                }}
              />
            ))
          )}
          {onManage && (
            <Chip
              size="small"
              label={store.clientPOs.length ? "Manage" : "Add PO"}
              onClick={onManage}
              variant="outlined"
              sx={{
                height: 26,
                borderRadius: "999px",
                fontSize: "0.75rem",
                fontWeight: 600,
                color: colors.accentPurple,
                borderColor: colors.accentPurple,
              }}
            />
          )}
        </Box>
        <InlineFormError message={store.downloadError || store.loadError} />
      </Box>
    );
  },
);

export default ClientPOSummary;
